/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';

@Component({
    selector: 'app-update-propulsion-scenario-with-ext-data',
    templateUrl: './update-propulsion-scenario-with-ext-data.component.html',
    styleUrl: './update-propulsion-scenario-with-ext-data.component.scss',
    standalone: false
})
export class UpdatePropulsionScenarioWithExtDataComponent implements OnInit {
  showSpinner: boolean;
  isPreprocess: boolean;
  isUpdateDates: boolean;

  file: File;
  sheets: string[];
  selectedSheet: string;

  constructor(public snackBar: FabMessageService,
              public readonly stateService: StateService,
              public readonly excelService: ExcelExportService,
              public readonly propulsionScenarioService: PropulsionScenarioService,
              public readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.isPreprocess = true;
    this.isUpdateDates = false;
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.selectedSheet = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.selectedSheet.length === 0) {
          this.file = null;
        } else {
          this.openUpdateConfirmation();
        }
      }
    );
  }

  /**
   * Opens the update confirmation to continue with updating the propulsion scenario with external data
   */
  openUpdateConfirmation(): void {
    // Open confirmation dialog to confirm you want to continue with the update
    const selectedScen = this.stateService.selectedScenario.propulsionScenario.id + '- ' + this.stateService.selectedScenario.propulsionScenario.propulsionScenarioName;
    let message = 'This will update the scenario ' + selectedScen  + '. Continue?';
    if (this.isPreprocess) {
      message = 'This will preview the changes without making updates to the scenario ' + selectedScen + '. Continue?';
    }
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmation',
        message: message,
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    }).afterClosed().subscribe({
      next: confirmation => {
        if (confirmation) {
          this.onConfirm()
        }
      }
    });
  }

  /**
   * Event handler for when user confirms the update
   */
  onConfirm(): void {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const fileName = 'UpdateExDataProp_' + this.stateService.selectedScenario.propulsionScenario.id + "_" + currDate;

    this.showSpinner = true;
    this.propulsionScenarioService.updateWithExternalData(this.file, this.selectedSheet, this.stateService.getUserGmid(),
      this.stateService.selectedScenario.propulsionScenario.id, this.isPreprocess, this.isUpdateDates).subscribe(
      {
        next: (data) => {
          this.excelService.exportToExcelFromServer(data, fileName);
        },
        error: (error) => {
          this.showSpinner = false;
          this.snackBar.displayError(error, DISMISS_MSG);
        },
        complete: () => {
          this.showSpinner = false;
          this.snackBar.displaySuccess('Successfully retrieved data. Exporting...', DISMISS_MSG);
        }
      }
    );
  }
}
